module Meevo.Plugins.Accountsync {
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;

    export enum ExternalAccountTypeEnum {
        ProductIncome = 1,
        ServiceIncome = 2,
        OtherIncome = 3,
        AccountReceivable = 4,
        InventoryAssets = 5,
        CostOfGoodsSold = 6,
        Liabilities = 7,
        ExpenseAccount = 8,
        CashDrawer, Bank = 9,
        Other = 101,
        Income = 10,
        OtherCurrentAsset = 11,
        OtherAsset = 12,
        FixedAsset = 13
    }

    export enum SyncProcessingStatusEnum {
        NotProcessed,
        InProcess,
        Complete,
        Failed,
        InError
    }

    enum SyncActionTypeEnum {
        Sync = 0,
        Delete = 1
    }

    export enum ExternalApplicationEnum {
        Quickbooks = 1
    }

    export enum ConnectionVersionEnum {
        Online,
        Local
    }

    // Main tab's interface
    export interface IConnectionListItem {
        applicationConnectionId: Guid;
        applicationConnectionDisplayName: string;
        entityVersion: number;
    }

    export interface IConnectionSetupInfo {
        applicationConnection: IApplicationConnection,
        applicationConnectionDetail?: IApplicationConnectionDetail
    }

    export interface IAccountsSetup {
        accountSetup: {
            accounts: Array<IAccountInfo>;
        };
        paymentTypeSetup: {
            items: Array<IPaymentAccount>;
        };
    }

    interface IApplicationConnection {
        applicationConnectionId: string;
        applicationConnectionDisplayName?: string;
        applicationConnectionUrl: string;
        applicationConnectionUserName?: string;
        applicationConnectionPassword?: string;
        undepositedFundsAccountExternalId: string;
        externalApplicationEnum: ExternalApplicationEnum;
        applicationConnectionTypeEnum: ConnectionVersionEnum;
        isActive: boolean;
        objectStateEnum: ObjectStateEnum;
        entityVersion: number;
        isInventoryManagementAllowed: boolean;
        includeMonetaryPayments: boolean;
    }

    interface IApplicationConnectionDetail {
        applicationConnectionDetailId: string;
        realmId: string;
        companyName: string;
        qbUserName: string;
        active: boolean;
        accessToken: string;
        accessTokenReceivedOn?: Date;
        refreshToken: string;
        refreshTokenReceivedOn?: Date;
        authenticatedOn: Date;
        updateAuthentificatedOnTime: boolean;
    }

    export interface IPaymentAccount {
        tenantId?: number,
        applicationConnectionId?: Guid,
        paymentTypeId: string,
        paymentTypeName?: string,
        entityVersion?: number,
        paymentTypeAccountExternalId: string,
        paymentTypeAccountExternalDisplayName: string,
        paymentTypeSId: number
    }

    export interface IQbLoginData {
        accessToken: string;
        active: boolean;   
        authCode: string;
        locationId: number;   
        realmId: string;
        refreshToken: string;
        tenantId: number;   
        user?: {
            email: string;
            givenName: string;
            familyName: string;
        }
    }

    interface IRequestRefreshToken {
        applicationConnectionId: string;
        applicationConnectionDetailId: string;
    }

    interface IUndepositedFundsAccountInfo {
        realmId: string;
        accessToken: string;
    }

    export interface IAccountSystemList {
        id: number;
        displayName: string;
        objectStateEnum: number;
        defaultAccountName: string;
        isDisabled: boolean;
        accountType: number;
    }

    interface IExternalAccountRequest {
        types: ExternalAccountTypeEnum[],
        tenantId: number;
        locationId: number;
        applicationConnectionId: Guid | string;
        applicationConnectionDetailId: Guid | string;
    }

    interface IPaymentTypeCriteria {
        criteria: {
            includeNonMonetary: number; 
        };
        pageNumber: number;
        itemsPerPage: number;
    }

    export interface IAccountInfo {
        accountExternalDisplayName: string;
        accountExternalFullName?: string;
        accountSId: number;
        accountSDisplayName: string;
        externalAccoutId?: string;
        tenantId?: number;
        applicationConnectionId?: string;
        accountId?: string;
        entityVersion?: number;
        accountExternalId?: string;
    }

    export interface IExternalAccount {
        id: string; 
        fullName: string;
        name: string;
        accountType: ExternalAccountTypeEnum;
    }

    export interface IAccountCountRequest {
        criteria: IAccountCountСriteria;
        asLocationId: number;
    }

    interface IAccountCountСriteria {
        displayName: string;
        url: string;
        realmId: string;
        objectState: ObjectStateEnum;
        filter: string;
        maintenanceMode: boolean;
        isGlobal: boolean;
    }

    interface IAccountsRetentionStatus {
        applicationConnectionId: Guid;
        items: IAccountRetentionStatus[];
    }

    interface IAccountRetentionStatus {
        entityDisplayName: string;
        messages: string[];
    }

    // Sync tab's interface
    export interface ISyncRequestData {
        id: Guid;
        startDate: Date | string;
        endDate: Date | string;
    }

    interface ISyncOperation {
        accountSyncQueueHeaderId: Guid;
        actionTypeEnum: SyncActionTypeEnum;
        applicationConnectionId: Guid;
        processingStatus: SyncProcessingStatusEnum;
        numAttempts: number;
        dateCreated: Date | string;
        dateToProcessFrom: Date | string;
        dateToProcessTo: Date | string;
    }

    export interface IAccountsyncService {
        getConnection: (applicationConnectionId: Guid) => ng.IPromise<any>;
        getConnectionsCount: (criteria: IAccountCountRequest) => ng.IPromise<number>;
        updateAccessToken: (criteria: IRequestRefreshToken) => ng.IPromise<IQbLoginData>;
        deleteConnection: (criteria: any) => ng.IPromise<IApplicationConnection>;
        saveConnectionInfo: (connectionInfo: IConnectionSetupInfo) => ng.IPromise<IConnectionSetupInfo>;
        updateConnectionInfo: (connectionInfo: IConnectionSetupInfo) => ng.IPromise<IConnectionSetupInfo>;
        testUndepositedFundsAccount: (accountInfo: IUndepositedFundsAccountInfo) => ng.IPromise<Guid>;
        getAccountSystemList: () => ng.IPromise<IAccountSystemList[]>;
        getExernalAccountsList: (criteria: IExternalAccountRequest) => ng.IPromise<IExternalAccount[]>;
        getPaymentTypeList: (requestParams: IPaymentTypeCriteria) => ng.IPromise<Meevo.Model.PaymentTypeListItem[]>;
        addAccountsToConnection: (accountsInfo: IConnectionSetupInfo & IAccountsSetup) => ng.IPromise<IAccountsRetentionStatus>;
        syncData: (syncData: ISyncRequestData) => ng.IPromise<string>;
        getLastSyncData: (applicationConnectionId: Guid) => ng.IPromise<ISyncOperation>;
        deleteSync: (syncForRemoved: ISyncRequestData) => ng.IPromise<string>;
        getNotClosedDrawersList: (criteria: any) => ng.IPromise<any>;
        getTaxStatus(criteria: any): ng.IPromise<any>
    }


    export class AccountsyncService implements IAccountsyncService {
        static $inject = [
            'meevo.components.common.DataService',
            'meevo.AppSettings',
            'meevo.components.tracking.TrackingService'
        ];

        constructor(
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected trackingService: Meevo.Components.Tracking.ITrackingService
        ) { }

        /**
         * Get connection using id
         * @param {Guid} applicationConnectionId
         * @returns {ng.IPromise<any>} promise
         */
        // Todo: added type of return statment
        getConnection(applicationConnectionId: Guid): ng.IPromise<any> {
            return this.dataService.getItem(this.appSettings.API_ENDPOINTS.accountsync, applicationConnectionId);
        }

        /**
         * Get count of connections
         * @param {IAccountCount} criteria
         * @returns {ng.IPromise<number>} promise
         */
        getConnectionsCount(criteria: IAccountCountRequest): ng.IPromise<number> {
            return this.dataService.getCount(this.appSettings.API_ENDPOINTS.accountsync, criteria);
        }

        /**
         * Update access token for connection
         * @param {IRequestRefreshToken} criteria
         * @returns {ng.IPromise<IQbLoginData>} promise
         */
        updateAccessToken(criteria: IRequestRefreshToken): ng.IPromise<IQbLoginData> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/GetQBConnection`, 'POST', criteria)
                .then(response => response.data);
        }

        /**
         * Delete connection using id
         * @param {any} criteria
         * @returns {ng.IPromise<IApplicationConnection>} promise
         */
        deleteConnection(criteria: any): ng.IPromise<IApplicationConnection> {
            return this.dataService.patch(this.appSettings.API_ENDPOINTS.accountsync, criteria);
        }

        /**
         * Save connection info
         * @param {IConnectionSetupInfo} connectionInfo
         * @returns {ng.IPromise<IConnectionSetupInfo>} promise
         */
        saveConnectionInfo(connectionInfo: IConnectionSetupInfo): ng.IPromise<IConnectionSetupInfo> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsync}/ConnectionSetup`, connectionInfo);
        }

        /**
         * Update connection info
         * @param {IConnectionSetupInfo} connectionInfo
         * @returns {ng.IPromise<IConnectionSetupInfo>} promise
         */
        updateConnectionInfo(connectionInfo: IConnectionSetupInfo): ng.IPromise<IConnectionSetupInfo> {
            return this.dataService.update(`${this.appSettings.API_ENDPOINTS.accountsync}/ConnectionSetup`, connectionInfo);
        }

        /**
         * Test connection with QB using connection's credentials
         * @param {IUndepositedFundsAccountInfo} accountInfo
         * @returns {ng.IPromise<Guid>} promise
         */
        testUndepositedFundsAccount(accountInfo: IUndepositedFundsAccountInfo): ng.IPromise<Guid> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/UndepositedFundsAccount`, 'POST', accountInfo)
                .then(response => response.data);
        }

        /**
         * Get list of System Account
         * @returns {ng.IPromise<IAccountSystemList[]>} promise
         */
        getAccountSystemList(): ng.IPromise<IAccountSystemList[]> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/AccountSystem/List`, 'GET', null)
                .then(response => response.data);
        }

        /**
         * Get list of Exernal Account for System Account
         * @param {IExternalAccountRequest} criteria
         * @returns {ng.IPromise<IExternalAccount[]>} promise
         */
        getExernalAccountsList(criteria: IExternalAccountRequest): ng.IPromise<IExternalAccount[]> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/ExternalAccounts/List`, 'POST', criteria)
                .then(response => response.data);     
        }

        /**
         * Get list of Payment Type
         * @param {IPaymentTypeCriteria} accountTypeId
         * @returns {ng.IPromise<Meevo.Model.PaymentTypeListItem[]>} promise
         */
        getPaymentTypeList(requestParams: IPaymentTypeCriteria): ng.IPromise<Meevo.Model.PaymentTypeListItem[]> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.paymentType}/List/`, 'POST', requestParams)
                .then(response => response.data);  
        }        

        /**
         * Attached accounts to connection
         * @param {IConnectionSetupInfo & IAccountsSetup} accountsInfo
         * @returns {ng.IPromise<IAccountsRetentionStatus>} promise
         */
        addAccountsToConnection(connection: IConnectionSetupInfo & IAccountsSetup): ng.IPromise<IAccountsRetentionStatus> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsync}/AccountSetup`, connection);
        }

        // Sync Tab

        /**
         * Sync data for chosen connection
         * @param {ISyncRequestData} syncData
         * @returns {ng.IPromise<string>} promise
         */
        syncData(syncData: ISyncRequestData): ng.IPromise<string> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/Sync`, 'POST', syncData)
                .then(response => response.data);
        }

        /**
         * Get the information about the last sync for chosen connection
         * @param {Guid} applicationConnectionId
         * @returns {ng.IPromise<ISyncOperation>} promise
         */
        getLastSyncData(applicationConnectionId: Guid): ng.IPromise<ISyncOperation> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/LastSync`, 'POST', { applicationConnectionId })
                .then(response => response.data);
        }

        /**
         * Delete sync from the server for chosen connection
         * @param {ISyncRequestData} syncForRemoved
         * @returns {ng.IPromise<string>} promise
         */
        deleteSync(syncForRemoved: ISyncRequestData): ng.IPromise<string> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/DeleteSync`, 'POST', syncForRemoved)
                .then(response => response.data);
        }

        /**
         * Get not closed drawers list
         * @param {any} criteria
         * @returns {ng.IPromise<any>} promise
         */
        getNotClosedDrawersList(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.drawerSession}/List`, 'POST', criteria)
                .then(response => {
                    return response.data.filter(drawer => !drawer.closedDate);
                });
        }

        /**
         * Get tax status
         * @param {any} criteria
         * @returns {ng.IPromise<any>} promise
         */
        getTaxStatus(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsync}/TaxStatus`, 'POST', criteria)
                .then(response => response.data);
        }


    }

    angular.module('meevo.plugins.accountsync')
        .service('meevo.plugins.accountsync.AccountsyncService', AccountsyncService);
}
